import ExcelJS from 'exceljs'
import { normalizeDutyRate, parseCustomsFee, type BrokerAnswerRow } from '@crm/shared'
import { readCellText } from '../../../shared/utils/excelCellText'
import { BROKER_SUBJECT_PREFIX } from './brokerExcel'

/**
 * Разбор Excel-ответа брокера по нетарифке (Л2, бриф §5.2).
 *
 * Колонки ищем по заголовкам, а не по номерам: брокер может добавить свою
 * колонку или переставить их. Строка заголовков — первая, где нашлась колонка
 * артикула. Числа («0,08», «73860») разбирают те же функции из @crm/shared,
 * что и сервер. Код запроса берётся из свойств файла (`subject`), если брокер
 * ответил в нашем же файле.
 *
 * Строка, где брокер ничего не ответил (пусты Нетарифка, Сертификат,
 * Особенности, Сбор, Пошлина и ТН ВЭД; «0» — значение), не уходит на сервер:
 * иначе наш же файл или частичный ответ записался бы как пустой ответ и
 * затёр память по партномеру. ТН ВЭД в строке оставляем — совпадает ли он с
 * отправленным, решает сервер.
 */

type AnswerField = keyof BrokerAnswerRow

/** Подстроки заголовка (без регистра) → поле. Порядок важен: колонка — первому совпавшему полю. */
const HEADER_KEYS: ReadonlyArray<readonly [AnswerField, readonly string[]]> = [
  ['partNumber', ['артикул', 'партномер', 'part']],
  ['brand', ['производ', 'бренд', 'brand']],
  ['description', ['описан']],
  ['tnvedCode', ['тнвд', 'тн вэд', 'тнвэд', 'код тн']],
  ['nonTariffNote', ['нетариф']],
  ['certificateNote', ['сертиф']],
  ['featuresNote', ['особен']],
  ['customsFee', ['сбор']],
  ['dutyRate', ['пошлин']],
]

/** Пределы длины — как в схеме UploadBrokerAnswerSchema: длиннее — сервер отклонит весь файл. */
const TEXT_MAX: Record<Exclude<AnswerField, 'customsFee' | 'dutyRate'>, number> = {
  partNumber: 200,
  brand: 200,
  description: 2000,
  tnvedCode: 40,
  nonTariffNote: 5000,
  certificateNote: 5000,
  featuresNote: 5000,
}
const FEE_MAX = 10_000_000
/** Строку заголовков ищем не дальше этой строки — ниже уже данные. */
const HEADER_SCAN_ROWS = 20

const SUBJECT_RE = new RegExp(`^${BROKER_SUBJECT_PREFIX}([\\w-]+)$`)
// eslint-disable-next-line no-control-regex -- намеренно: сервер отклоняет управляющие символы (SAFE_STRING)
const CONTROL_CHARS_RE = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g

export interface ParsedBrokerResponse {
  /** Код запроса из свойств файла (наш файл вернулся от брокера) или null. */
  fileCode: string | null
  rows: BrokerAnswerRow[]
  /** Непустые строки без артикула — пропущены. */
  skipped: number
  /** Строки с артикулом, но без ответа брокера (заполнена только наша левая часть) — пропущены. */
  emptyRows: number
}

/** Есть ли в строке хоть что-то от брокера: тексты, сбор, пошлина или ТН ВЭД. */
export function hasBrokerAnswer(row: BrokerAnswerRow): boolean {
  return (
    !!row.tnvedCode ||
    !!row.nonTariffNote ||
    !!row.certificateNote ||
    !!row.featuresNote ||
    row.customsFee != null ||
    row.dutyRate != null
  )
}

function normalizeHeader(text: string): string {
  return text.toLowerCase().replace(/ё/g, 'е').replace(/\s+/g, ' ').trim()
}

type ColumnMap = Partial<Record<AnswerField, number>>

function mapHeaderRow(row: ExcelJS.Row): ColumnMap {
  const map: ColumnMap = {}
  row.eachCell({ includeEmpty: false }, (cell, col) => {
    const header = normalizeHeader(readCellText(cell))
    if (!header) return
    for (const [field, keys] of HEADER_KEYS) {
      if (map[field] != null) continue
      if (keys.some((k) => header.includes(k))) {
        map[field] = col
        return
      }
    }
  })
  return map
}

/**
 * Строка заголовков: первая с колонкой артикула и ещё хотя бы одной знакомой
 * колонкой (так строка-название вроде «Запрос по артикулам» не сойдёт за шапку);
 * если такой нет — первая, где нашёлся хотя бы артикул.
 */
function findHeader(ws: ExcelJS.Worksheet): { rowNumber: number; map: ColumnMap } | null {
  let fallback: { rowNumber: number; map: ColumnMap } | null = null
  const last = Math.min(ws.rowCount, HEADER_SCAN_ROWS)
  for (let r = 1; r <= last; r++) {
    const map = mapHeaderRow(ws.getRow(r))
    if (map.partNumber == null) continue
    if (Object.keys(map).length >= 2) return { rowNumber: r, map }
    fallback ??= { rowNumber: r, map }
  }
  return fallback
}

function clip(text: string, max: number): string | null {
  const clean = text.replace(CONTROL_CHARS_RE, '').trim()
  return clean ? clean.slice(0, max) : null
}

/** Число из ячейки-числа (или формулы с числовым результатом); иначе null. */
function numericValue(cell: ExcelJS.Cell): number | null {
  const v = cell.value
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (v && typeof v === 'object' && 'result' in v && typeof v.result === 'number') return v.result
  return null
}

function readDutyRate(cell: ExcelJS.Cell): number | null {
  // Ячейка-число: 0.08 так и остаётся долей, 8 (проценты) → 0.08 — правило normalizeDutyRate.
  const n = numericValue(cell)
  return normalizeDutyRate(n ?? (readCellText(cell) || null))
}

function readCustomsFee(cell: ExcelJS.Cell): number | null {
  const n = numericValue(cell)
  const text = readCellText(cell).replace(/(руб\.?|р\.|₽)/gi, '')
  const fee = parseCustomsFee(n ?? (text.trim() || null))
  return fee != null && fee <= FEE_MAX ? fee : null
}

async function loadWorkbook(file: File): Promise<ExcelJS.Workbook> {
  const wb = new ExcelJS.Workbook()
  try {
    await wb.xlsx.load(await file.arrayBuffer())
  } catch {
    throw new Error('Не удалось прочитать файл. Сохраните его в формате .xlsx и загрузите снова')
  }
  return wb
}

export async function parseBrokerResponse(file: File): Promise<ParsedBrokerResponse> {
  const wb = await loadWorkbook(file)
  const ws = wb.worksheets[0]
  if (!ws) throw new Error('В файле нет ни одного листа')

  const subject = String(wb.subject ?? '').trim()
  const fileCode = SUBJECT_RE.exec(subject)?.[1] ?? null

  const header = findHeader(ws)
  if (!header) throw new Error('В файле не найдена колонка «Артикул» — проверьте заголовки')
  const { map } = header
  const partCol = map.partNumber!

  const text = (row: ExcelJS.Row, field: keyof typeof TEXT_MAX): string | null => {
    const col = map[field]
    return col == null ? null : clip(readCellText(row.getCell(col)), TEXT_MAX[field])
  }

  const rows: BrokerAnswerRow[] = []
  let skipped = 0
  let emptyRows = 0
  for (let r = header.rowNumber + 1; r <= ws.rowCount; r++) {
    const row = ws.getRow(r)
    const partNumber = text(row, 'partNumber')
    if (!partNumber) {
      const hasData = Object.values(map).some(
        (col) => col !== partCol && readCellText(row.getCell(col)) !== '',
      )
      if (hasData) skipped++
      continue
    }
    const parsed: BrokerAnswerRow = {
      partNumber,
      brand: text(row, 'brand'),
      description: text(row, 'description'),
      tnvedCode: text(row, 'tnvedCode'),
      nonTariffNote: text(row, 'nonTariffNote'),
      certificateNote: text(row, 'certificateNote'),
      featuresNote: text(row, 'featuresNote'),
      customsFee: map.customsFee == null ? null : readCustomsFee(row.getCell(map.customsFee)),
      dutyRate: map.dutyRate == null ? null : readDutyRate(row.getCell(map.dutyRate)),
    }
    if (hasBrokerAnswer(parsed)) rows.push(parsed)
    else emptyRows++
  }

  if (rows.length === 0 && emptyRows > 0) {
    throw new Error('В файле нет ответов брокера: заполнены только артикулы')
  }

  return { fileCode, rows, skipped, emptyRows }
}
